import type { Navigator, Screen } from "../app/screen";
import { Api, ApiError } from "../net/api";
import { choiceList, note, pageShell, type Choice } from "../ui/page";
import { animatePlates, type PlateAnimator } from "../fx/plate";

/**
 * THE ASCENT - five champions, climbed in order.
 *
 * ⭐ **THE LADDER IS SERVED, NOT HARDCODED.** `Gauntlet/Progress` returns
 * `{highestLevelCompleted, levels:[{level, name, bossName, botDeckId, isUnlocked, isCompleted}]}`.
 * Rendering from that means adding a sixth champion is a server change, not a client release - which
 * is the §3b question asked of this screen: to add a rung, how many places change? One.
 *
 * ⚠️ **A LOCKED RUNG IS DRAWN LOCKED AND SAYS WHY.** The old client drew every rung live and let the
 * server refuse, so a player pressed a boss and nothing happened. `isUnlocked` is in the payload; use
 * it.
 *
 * ⛔ **THE FOURFOLD TRIAL IS NOT HERE.** It is the campaign Alex paused, and it is not offered rather
 * than being offered and refusing - see `docs/` and the old `RevivalCampaignScreen`, which marked it
 * COMING SOON for the same reason.
 */

interface GauntletLevel {
  level: number;
  name?: string;
  bossName?: string;
  isUnlocked?: boolean;
  isCompleted?: boolean;
  /** What this rung pays, in the server's own words. See the reward panel below. */
  rewardText?: string;
}

export const CampaignScreen: Screen = {
  id: "campaign",
  mustOffer: ["BACK"],

  async mount(host: HTMLElement, nav: Navigator): Promise<void> {
    const { el, body } = pageShell(nav, "THE ASCENT", "face five champions");
    host.append(el);

    const loading = note("Reading the ladder...");
    body.append(loading);
    anim = animatePlates(el);

    let progress: { levels?: GauntletLevel[]; highestLevelCompleted?: number };
    try {
      progress = (await Api.gauntletProgress()) as typeof progress;
    } catch (e) {
      // ⛔ THE SERVER'S OWN WORDS. A generic "something went wrong" for every cause is what cost days.
      loading.replaceWith(
        note(e instanceof ApiError ? e.body || e.message : String(e), true),
      );
      return;
    }

    const levels = Array.isArray(progress.levels) ? progress.levels : [];
    if (levels.length === 0) {
      loading.replaceWith(
        note("The ladder is empty. Gauntlet/Progress returned no levels.", true),
      );
      return;
    }

    const choices: Choice[] = levels.map((lv) => {
      const title = lv.bossName || lv.name || `RUNG ${lv.level}`;
      if (lv.isCompleted) {
        return { label: title, sub: "defeated", onPick: () => start(lv.level) };
      }
      if (!lv.isUnlocked) {
        return { label: title, disabled: "locked" };
      }
      return { label: title, sub: "ready", onPick: () => start(lv.level) };
    });

    loading.replaceWith(choiceList(choices));

    // ⭐ **WHAT THIS LADDER PAYS - THE ASCENT'S OWN REWARDS, NOT ANOTHER CAMPAIGN'S.**
    //
    // 🚨 **THIS PANEL USED TO SHOW THE FIVE REALMS' MILESTONES ON THE ASCENT'S PAGE.** It called
    // `Api.campaignProgress()`, whose `milestones` are keyed `quadrant_1..4`, `ironside_city`,
    // `three_star_mastery` - progress markers for a completely different mode. Alex, 2026-08-15,
    // after beating the first champion: *"here it is showing what you win, but from a different
    // campaign or from these campaigns? how are these connected and since I won the first already
    // shouldn't I get something?"*
    //
    // The honest answers were: they are NOT connected, and no, he got nothing. Every row read
    // "locked" and always would, because nothing you do on this screen can advance a quadrant.
    //
    // ⛔ **AND THE LADDER PAID NOTHING AT ALL.** `AwardGauntletWin` calls
    // `Rewards.Parse(GauntletRewardText(...))`, which looks for `rewardText` on the level - and no
    // level had one. The whole payment path was built and wired and the DATA was absent, so it
    // parsed null, found `IsEmpty`, and paid nothing, silently, on every win since the mode existed.
    // The rungs now carry `rewardText` in `server-capture/Gauntlet__Progress.json`.
    //
    // ⚠️ **THE SERVER'S WORDS, NOT OURS.** Restating a reward here would be a second owner of the
    // reward table and it would be wrong the first time one is balanced.
    showLadderRewards(levels);

    function showLadderRewards(lv: GauntletLevel[]): void {
      const paid = lv.filter((x) => (x.rewardText ?? "").trim().length > 0);
      // ⛔ Nothing invented when the data is absent: a reward panel that lists rewards the server
      // will not pay is exactly the defect above, rebuilt.
      if (paid.length === 0) return;

      const panel = document.createElement("section");
      panel.className = "campaign__panel";

      const head = document.createElement("h2");
      head.className = "campaign__rewardhead";
      head.textContent = "WHAT YOU WIN";

      const nft = document.createElement("p");
      nft.className = "campaign__rewardnote";
      nft.textContent =
        "Cards you win are yours to keep, and yours to mint. Won cards become NFTs on Polygon and "
        + "BSC, held in your own wallet and tradable at the Revival Tent on Wanted. Each card can be "
        + "won once per account, so the supply is exactly the number of players who beat the rung.";

      const list = document.createElement("div");
      list.className = "campaign__rewards";

      for (const x of paid) {
        const row = document.createElement("div");
        const done = x.isCompleted === true;
        row.className = "campaign__reward" + (done ? " campaign__reward--claimed" : "");

        const t = document.createElement("span");
        t.className = "campaign__rewardtitle";
        t.textContent = x.bossName || x.name || `Rung ${x.level}`;

        const d = document.createElement("span");
        d.className = "campaign__rewardwhat";
        d.textContent = String(x.rewardText ?? "");

        // ⭐ "PAID", not "claimed": this ladder pays on the win itself, with no button to press.
        // Calling it claimable would advertise a control that does not exist.
        row.append(t, d, status(done ? "paid" : "locked"));
        list.append(row);
      }

      panel.append(head, nft, list);
      body.append(panel);
    }

    function status(text: string): HTMLElement {
      const s2 = document.createElement("span");
      s2.className = "campaign__rewardstate";
      s2.textContent = text;
      return s2;
    }
    // ⚠️ Re-armed AFTER the plates exist. `animatePlates` snapshots the list at call time, so arming
    // it before the fetch resolved would leave every rung inert - the plate would draw and never
    // respond, which is indistinguishable from a dead control.
    anim?.stop();
    anim = animatePlates(el);

    async function start(level: number): Promise<void> {
      const busy = note(`Starting rung ${level}...`);
      body.append(busy);
      try {
        await Api.gauntletStartLevel(level);
        nav.go("match");
      } catch (e) {
        busy.replaceWith(note(e instanceof ApiError ? e.body || e.message : String(e), true));
      }
    }
  },

  unmount(): void {
    anim?.stop();
    anim = null;
  },
};

let anim: PlateAnimator | null = null;
